import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import {
  clearFlightTrack,
  fetchFlightTrack,
  FlightTrackStatus,
} from '../store/slices/flightTrackSlice';
import { FlightTrack } from '../types/flightTrack';

export interface SelectedDroneTrack {
  track: FlightTrack | null;
  status: FlightTrackStatus;
  error: string | null;
}

/**
 * The flown track of whichever drone is selected (#581).
 *
 * The fetch lives in a hook rather than in the map component because the map
 * needs the data in two places that cannot be the same element: the polyline
 * goes inside `MapContainer`, and the message about *why there is no polyline*
 * has to go outside it — MUI's `Paper` does not render within Leaflet's
 * container, which is why every notice in `DroneMap` sits in the overlay
 * column. One owner of the request, two consumers.
 *
 * Selecting a different drone replaces the track; deselecting clears it. Both
 * matter more than they look: a track left behind after deselection is a path
 * on the map belonging to no aircraft the pilot is looking at.
 */
export const useSelectedDroneTrack = (): SelectedDroneTrack => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedDroneId = useSelector(
    (state: RootState) => state.drones.selectedDroneId
  );
  const { track, status, error } = useSelector(
    (state: RootState) => state.flightTrack
  );

  useEffect(() => {
    if (!selectedDroneId) {
      dispatch(clearFlightTrack());
      return;
    }
    dispatch(fetchFlightTrack(selectedDroneId));
  }, [selectedDroneId, dispatch]);

  return { track, status, error };
};
